/**
 * MobileQuickActionsFab.js — Burbuja flotante para móvil (Iter 59 · Jun 2026).
 *
 * Cambios vs versión anterior:
 *   • Rutas corregidas: admin usa `/admin/remesas/enviar` y `/admin/remesas/conversor`
 *     (las anteriores `/admin/remesas/nueva` y `/calculadora` no existían y por
 *     eso al tocar Enviar/Conversor no pasaba nada).
 *   • Arrastrable: long-press (350ms) entra en "modo mover" y la burbuja sigue
 *     al dedo / mouse. La posición queda persistida en localStorage para que
 *     el CEO no la tenga que reubicar cada vez.
 *   • Snap automático al borde derecho/izquierdo más cercano al soltar.
 *   • Click rápido sigue abriendo el menú; el modo arrastre NO dispara click.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Calculator, X, Zap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'fab.position.v1';
const BUBBLE = 56;       // px (w-14 h-14)
const MARGIN = 16;       // distance from viewport edges
const LONG_PRESS_MS = 350;

const defaultPosFor = (role) => {
  // Right-bottom by default. Admin ↑ to avoid the AiAssistant chat (bottom-6).
  const w = typeof window !== 'undefined' ? window.innerWidth : 360;
  const h = typeof window !== 'undefined' ? window.innerHeight : 640;
  return {
    x: w - BUBBLE - MARGIN,
    y: h - BUBBLE - (role === 'admin' ? 96 : MARGIN + 24),
  };
};

const clampToViewport = ({ x, y }) => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return {
    x: Math.max(MARGIN, Math.min(x, w - BUBBLE - MARGIN)),
    y: Math.max(MARGIN, Math.min(y, h - BUBBLE - MARGIN)),
  };
};

const MobileQuickActionsFab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY);
      if (raw) return clampToViewport(JSON.parse(raw));
    } catch (_) { /* ignore */ }
    return defaultPosFor(user?.role);
  });
  const dragStateRef = useRef({ moved: false, longPressTimer: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });

  // Keep the bubble inside the viewport when the device rotates.
  useEffect(() => {
    const onResize = () => setPos((p) => clampToViewport(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Persist on every position change after the user finishes dragging.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch (_) { /* ignore */ }
  }, [pos]);

  // Attach window-level move/up while dragging — declared BEFORE any early
  // return to comply with React's rules-of-hooks (must run on every render).
  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const point = e.touches ? e.touches[0] : e;
      if (e.cancelable) e.preventDefault();
      const ds = dragStateRef.current;
      setPos(clampToViewport({ x: point.clientX - ds.offsetX, y: point.clientY - ds.offsetY }));
    };
    const up = () => {
      const ds = dragStateRef.current;
      if (ds.longPressTimer) { clearTimeout(ds.longPressTimer); ds.longPressTimer = null; }
      setDragging(false);
      setPos((p) => {
        const mid = window.innerWidth / 2;
        return clampToViewport({
          x: p.x + BUBBLE / 2 < mid ? MARGIN : window.innerWidth - BUBBLE - MARGIN,
          y: p.y,
        });
      });
    };
    window.addEventListener('mousemove',  move, { passive: false });
    window.addEventListener('touchmove',  move, { passive: false });
    window.addEventListener('mouseup',    up);
    window.addEventListener('touchend',   up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup',   up);
      window.removeEventListener('touchend',  up);
    };
  }, [dragging]);

  if (!user) return null;
  const role = user.role;
  if (role !== 'gestor' && role !== 'admin') return null;

  const ACTIONS = role === 'admin'
    ? [
        { icon: Send,       label: 'Enviar Remesa', path: '/admin/remesas/enviar',     testid: 'fab-send-remesa' },
        { icon: Calculator, label: 'Conversor',     path: '/admin/remesas/conversor',  testid: 'fab-conversor' },
      ]
    : [
        { icon: Send,       label: 'Enviar Remesa', path: '/remesas/enviar',    testid: 'fab-send-remesa' },
        { icon: Calculator, label: 'Conversor',     path: '/remesas/conversor', testid: 'fab-conversor' },
      ];

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  // ── Pointer interaction model
  // - press → start a long-press timer; if it fires before pointer-up, we enter
  //   drag mode and the bubble follows the pointer.
  // - up before timer → it counts as a click → toggle open.
  // - up after timer → drag ends; click is suppressed.
  const onPointerDown = (e) => {
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    const ds = dragStateRef.current;
    ds.moved = false;
    ds.startX = point.clientX;
    ds.startY = point.clientY;
    ds.offsetX = point.clientX - pos.x;
    ds.offsetY = point.clientY - pos.y;
    ds.longPressTimer = setTimeout(() => {
      setDragging(true);
      ds.moved = true;
      if (navigator.vibrate) {
        try { navigator.vibrate(30); } catch (_) { /* best effort */ }
      }
    }, LONG_PRESS_MS);
  };
  const onPointerUp = () => {
    const ds = dragStateRef.current;
    if (ds.longPressTimer) {
      clearTimeout(ds.longPressTimer);
      ds.longPressTimer = null;
    }
    if (dragging || ds.moved) return; // drag end handled by window listener
    setOpen((v) => !v);
  };

  // The expanded shortcuts open ABOVE the bubble. We compute the menu's
  // anchor based on which half of the screen the bubble lives in.
  const isRightSide = pos.x + BUBBLE / 2 > (typeof window !== 'undefined' ? window.innerWidth / 2 : 180);
  const menuStyle = {
    position: 'fixed',
    bottom: typeof window !== 'undefined' ? Math.max(MARGIN, window.innerHeight - pos.y + 8) : MARGIN,
    [isRightSide ? 'right' : 'left']: MARGIN,
    zIndex: 41,
  };

  return (
    <>
      {/* Backdrop while the menu is open (tap to dismiss). */}
      {open && (
        <button
          type="button"
          aria-label="Cerrar"
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          data-testid="fab-backdrop"
        />
      )}

      {/* Expanded shortcuts — anchored relative to the bubble's current pos. */}
      {open && (
        <div
          style={menuStyle}
          className={`flex flex-col gap-2 ${isRightSide ? 'items-end' : 'items-start'} lg:hidden`}
          data-testid="fab-menu"
        >
          {ACTIONS.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={(e) => { e.stopPropagation(); go(a.path); }}
              className="flex items-center gap-2 bg-white border border-emerald-200 text-slate-900 pl-3 pr-4 py-2 rounded-full shadow-lg hover:bg-emerald-50 active:scale-95 transition-all"
              data-testid={a.testid}
            >
              <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <a.icon className="w-4 h-4" />
              </span>
              <span className="text-sm font-semibold">{a.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* The bubble itself — absolutely positioned to (pos.x, pos.y). */}
      <button
        type="button"
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        onMouseUp={!dragging ? onPointerUp : undefined}
        onTouchEnd={!dragging ? onPointerUp : undefined}
        style={{
          position: 'fixed',
          left: pos.x,
          top:  pos.y,
          touchAction: 'none',
          transition: dragging ? 'none' : 'left 240ms ease, top 240ms ease, transform 100ms',
          zIndex: 42,
        }}
        className={`w-14 h-14 rounded-full text-white shadow-2xl flex items-center justify-center select-none lg:hidden ${
          dragging
            ? 'bg-amber-500 ring-4 ring-amber-200 scale-110 cursor-grabbing'
            : open
              ? 'bg-rose-600 hover:bg-rose-700'
              : 'bg-gradient-to-br from-emerald-500 to-teal-700 hover:scale-110'
        }`}
        data-testid="mobile-fab-toggle"
        aria-label={
          dragging ? 'Soltá para fijar la burbuja' :
            open ? 'Cerrar accesos rápidos' : 'Abrir accesos rápidos · mantené presionado para mover'
        }
      >
        {open ? <X className="w-6 h-6" /> : <Zap className="w-6 h-6" />}
      </button>
    </>
  );
};

export default MobileQuickActionsFab;
